import { createContext, FunctionComponent } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import * as z from "zod";

// Merged configuration schema supporting both chat and voice widget options
const Config = z.object({
  // Required
  agentId: z.string({
    error: (issue) => issue.input === undefined ? "Agent ID is required" : "Invalid agent ID",
  }),
  
  // Common positioning and styling
  position: z.enum(["bottom-left", "bottom-right"]).default("bottom-right"),
  zIndex: z.number().default(999),

  // Widget config options
  chatOptions: z.enum(["chat", "voice", "chat_and_voice"]).default("chat_and_voice"),
  textColor: z.string().default("#273141"),
  buttonTextColor: z.string().default("#fff"),
  primaryColor: z.string().default("#000"),
  buttonText: z.string().optional(),
  closeButtonText: z.string().optional(),
  buttonSize: z.number().default(48),
  width: z.number().default(400),
  height: z.number().default(600),
  autoOpen: z.boolean().default(false),
  cookieDuration: z.number().nullable().default(1209600), // 2 weeks
  titleText: z.string().nullable().default("How can we help?"),
  subtitleText: z.string().nullable().default("Choose your preferred way to connect with us"),

  voiceTitleText: z.string().nullable().default("Voice Assistant"),
  voiceSubtitleText: z.string().nullable().default("Talk with our AI assistant"),
  voiceHelperText: z.string().nullable().default("We'll use this to personalize your conversation"),

  chatButtonText: z.string().nullable().default("Start Chat"),
  chatButtonDescriptionText: z.string().nullable().default("Text-based conversation"),

  voiceButtonText: z.string().nullable().default("Voice Call"),
  voiceButtonDescriptionText: z.string().nullable().default("Talk with our AI assistant"),
  
  startVoiceConversationText: z.string().nullable().default("Start Voice Conversation"),
  endVoiceConversationText: z.string().nullable().default("End Conversation"),

  showEmailCollectionForm: z.boolean().default(true),
});

export type Config = z.infer<typeof Config>;

type ConfigContext = {
  config: Config | null;
  updateConfig: (updateConfig: unknown) => void;
}
type ConfigContextInitialised = ConfigContext & {
  config: Config;
}
const ConfigContext = createContext<ConfigContext | ConfigContextInitialised | null>(null);

export const ConfigProvider: FunctionComponent = ({ children }) => {
  const [configState, setConfigState] = useState<Config | null>(null);

  const updateConfig = (updateConfig: unknown) => {
    if (typeof updateConfig !== "object") {
      throw new Error("Invalid config");
    }
    const parsedConfig = Config.safeParse({...configState, ...updateConfig});
    if (parsedConfig.success) {
      setConfigState(parsedConfig.data);
    } else {
      console.error("Invalid config:", parsedConfig.error);
    }
  };

  useEffect(() => {
    // If the config is already set in the window object, update the config
    if (window.UnifiedWidgetConfig) {
      updateConfig(window.UnifiedWidgetConfig);
    }
    // Listen for changes to the UnifiedWidgetConfig object
    Object.defineProperty(window, 'UnifiedWidgetConfig', {
      set: (value) => updateConfig(value),
    });
  }, []);

  return (
    <ConfigContext.Provider value={{ config: configState, updateConfig }}>
      { configState && children}
    </ConfigContext.Provider>
  )
}

const isConfigContextInitialised = (context: ConfigContext | null): context is ConfigContextInitialised => {
  return context !== null && 'config' in context;
}

export const useConfig = (): ConfigContextInitialised => {
  const context = useContext(ConfigContext);

  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  if (!isConfigContextInitialised(context)) {
    throw new Error("Config not initalised");
  }

  return context;
};

